/**
 * HTTP İSTEMCİSİ.
 *
 * Üç şeyi merkezîleştirir; üçü de her istekte unutulabilecek şeylerdir:
 *
 *   1. Yetkilendirme başlığı ve AVM kapsamı (`X-Mall-Id`)
 *   2. 401 → tek-uçuş (single-flight) token yenileme
 *   3. Hata biçimi — her yerde aynı `ApiError`
 */

/*
  API KÖK ADRESİ.

  Varsayılan `/api/v1` GÖRELİDİR ve Vite proxy'si (geliştirmede) ya da ters
  vekil sunucu (üretimde) üzerinden aynı origin'e gider. Aynı origin olması
  iki şeyi kazandırır:

    - CORS ön kontrol (preflight) isteği hiç yapılmaz
    - httpOnly çerez `SameSite=Strict` ile taşınabilir

  Mutlak adres yalnızca API ayrı bir alan adındaysa gerekir; o durumda
  `VITE_API_URL` tam adresle ezilir.
*/
const BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

/** Göreli tabanı mutlak URL'e çevirmek için gereken origin. */
const ORIGIN = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;

/** Erişim token'ı YALNIZCA bellekte tutulur. */
let accessToken: string | null = null;

/** Aktif AVM kapsamı — istek araya girmeden ayarlanır. */
let currentMallId: string | null = null;

/** Oturum düştüğünde çağrılır (yönlendirme, store temizliği). */
let onUnauthorized: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setMallScope(mallId: string | null): void {
  currentMallId = mallId;
}

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

/**
 * TEK BİÇİMLİ HATA.
 *
 * Her ekran aynı alanları okur: `message` kullanıcıya gösterilir,
 * `status` davranışı belirler, `fieldErrors` form alanlarına dağıtılır.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly fieldErrors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Kullanıcıya gösterilecek Türkçe metin. */
  get userMessage(): string {
    if (this.status === 0) {
      return 'Sunucuya ulaşılamıyor. Bağlantınızı ve API servisinin çalıştığını kontrol edin.';
    }
    if (this.status === 403) {
      return this.message || 'Bu işlem için yetkiniz bulunmuyor.';
    }
    if (this.status === 404) {
      return this.message || 'Kayıt bulunamadı.';
    }
    if (this.status >= 500) {
      return 'Sunucuda beklenmeyen bir hata oluştu. Sorun sürerse sistem yöneticisine bildirin.';
    }
    return this.message;
  }
}

/*
  TEK-UÇUŞ TOKEN YENİLEME.

  Bir sayfa açılışında beş istek aynı anda 401 alabilir. Her biri ayrı
  yenileme başlatırsa, refresh token rotasyonu nedeniyle ilk yenileme
  diğerlerinin token'ını geçersiz kılar ve kullanıcı sebepsiz yere
  dışarı atılır.

  Bu yüzden yenileme TEK bir söz (promise) üzerinden yürür; bekleyenler
  aynı sözü paylaşır.
*/
let refreshPromise: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(new URL(`${BASE_URL}/auth/refresh`, ORIGIN), {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
      });

      if (!res.ok) return false;

      const data = (await res.json()) as { accessToken?: string };
      if (!data.accessToken) return false;

      accessToken = data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      // Bir sonraki 401 yeni bir yenileme başlatabilsin.
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * SESSİZ OTURUM KURTARMA — TEK-UÇUŞ YENİLEMENİN AYNISI.
 *
 * Açılışta oturumu kurtaran kod, kendi `POST /auth/refresh` isteğini
 * atıyordu. Bu, tek-uçuş kuyruğunu ATLIYORDU ve iki çağrı üst üste
 * geldiğinde (React StrictMode geliştirmede efektleri iki kez çalıştırır,
 * ya da iki sekme aynı anda açılır) AYNI yenileme jetonu iki kez
 * kullanılıyordu.
 *
 * Sonuç sessiz değil, yıkıcıydı: sunucu jeton rotasyonu + tekrar kullanım
 * tespiti uyguluyor; ikinci istek "çalınmış jeton" sinyali sayılıp
 * KULLANICININ TÜM OTURUM AİLESİ İPTAL EDİLİYORDU. Kullanıcı açısından
 * görüntü şuydu: sayfayı her yenilediğinde giriş ekranına düşüyor.
 *
 * Aynı kapıdan geçmek sorunu tamamen ortadan kaldırır — ikinci çağrı
 * birincinin sözünü paylaşır, ağa ikinci bir istek çıkmaz.
 */
export function restoreSession(): Promise<boolean> {
  return refreshAccessToken();
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Bu istek AVM kapsamı gerektirmiyorsa (giriş, AVM listesi). */
  skipMallScope?: boolean;
  /** 401'de yenileme denenmesin (giriş ve yenileme uçları). */
  skipRefresh?: boolean;
  signal?: AbortSignal;
  /** Yanıtı ham `Response` olarak döndür (PDF indirme). */
  raw?: boolean;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  /*
    `new URL()` GÖRELİ ADRESLE TABAN İSTER.

    `new URL('/api/v1/auth/login')` — taban verilmediği için "Invalid URL"
    fırlatır. İkinci parametre olarak origin verildiğinde hem göreli hem
    mutlak taban çalışır: mutlak bir BASE_URL verildiğinde taban yok sayılır.
  */
  const url = new URL(`${BASE_URL}${path}`, ORIGIN);

  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  }

  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;
  if (currentMallId && !options.skipMallScope) headers['x-mall-id'] = currentMallId;

  const send = () =>
    fetch(url.toString(), {
      method: options.method ?? 'GET',
      headers,
      credentials: 'include',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });

  let response: Response;
  try {
    response = await send();
  } catch (error) {
    // Ağ hatası — sunucu kapalı, DNS, CORS. `status: 0` bunu ayırt eder.
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'Sunucuya ulaşılamadı');
  }

  // --- 401: token yenile ve BİR KEZ tekrar dene ------------------------------
  if (response.status === 401 && !options.skipRefresh) {
    const refreshed = await refreshAccessToken();

    if (refreshed) {
      headers.authorization = `Bearer ${accessToken}`;
      response = await send();
    } else {
      accessToken = null;
      onUnauthorized?.();
      throw new ApiError(401, 'Oturumunuz sona erdi. Lütfen tekrar giriş yapın.');
    }
  }

  if (options.raw) {
    if (!response.ok) throw await toApiError(response);
    return response as unknown as T;
  }

  if (response.status === 204) return undefined as T;

  if (!response.ok) throw await toApiError(response);

  const text = await response.text();
  if (!text) return undefined as T;

  return JSON.parse(text) as T;
}

async function toApiError(response: Response): Promise<ApiError> {
  let message = `İstek başarısız (${response.status})`;
  let code: string | undefined;
  let fieldErrors: Record<string, string[]> | undefined;

  try {
    const body = (await response.json()) as {
      message?: string | string[];
      code?: string;
      errors?: Record<string, string[]>;
    };

    if (Array.isArray(body.message)) {
      /*
        Zod doğrulama hataları dizi olarak gelir. İlkini mesaj yapıp
        tamamını alan hatalarına dağıtmak, formun hangi alanda hata
        olduğunu gösterebilmesini sağlar.
      */
      message = body.message[0] ?? message;
    } else if (body.message) {
      message = body.message;
    }

    code = body.code;
    fieldErrors = body.errors;
  } catch {
    // Gövde JSON değilse varsayılan mesajla devam.
  }

  return new ApiError(response.status, message, code, fieldErrors);
}

/** Kısa yollar. */
export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
};

/** Sunucunun standart liste zarfı. */
export interface ListEnvelope<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}
